import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Printer, Save, BookOpen } from "lucide-react";

/* Saves each plant's batch-system connection details and auto-fills the printable
   Batch System Connection Guide. Separate from the live Sysdyne/Dorner plant-agent
   connectors — this is the how-to reference record for all five systems. */
export default function ConnectorGuideCard() {
  const [schema, setSchema] = useState([]);
  const [cfg, setCfg] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/connectors/schema").then((r) => setSchema(r.data.systems || [])).catch(() => {});
    api.get("/connectors/config").then((r) => setCfg(r.data.config || {})).catch(() => {});
  }, []);

  const setVal = (sk, fk, v) => setCfg((c) => ({ ...c, [sk]: { ...(c[sk] || {}), [fk]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/connectors/config", { config: cfg });
      toast.success("Connection details saved — the guide will now print with these values");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-industrial p-5" data-testid="connector-guide-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Batch System Connection Reference</div>
          </div>
          <p className="text-sm text-muted-industrial max-w-2xl">
            Record how this plant is wired to each batching system. These values auto-fill the printable
            Batch System Connection Guide, so it works as both a how-to and a live record of your setup.
            Leave anything you don't use blank.
          </p>
        </div>
        <a href={`${API_BASE}/connectors/guide.pdf`} target="_blank" rel="noreferrer" data-testid="connector-guide-pdf-link"
          className="inline-flex items-center gap-2 h-9 px-3 border border-industrial text-xs font-bold uppercase tracking-wide hover:bg-surface">
          <Printer className="w-3.5 h-3.5" /> Open guide PDF
        </a>
      </div>

      <div className="mt-4 space-y-4">
        {schema.map((s) => (
          <div key={s.key} className="border border-industrial p-4" data-testid={`connector-guide-${s.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="heading text-sm font-black uppercase">{s.name}</div>
              <span className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{s.tag}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {s.fields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-muted-industrial">{f.label}</Label>
                  <Input
                    data-testid={`connector-guide-${s.key}-${f.key}`}
                    value={(cfg[s.key] || {})[f.key] || ""}
                    onChange={(e) => setVal(s.key, f.key, e.target.value)}
                    placeholder="—"
                    className="h-9 rounded-none bg-transparent border-industrial mt-1 mono text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={busy} data-testid="connector-guide-save-btn"
        className="mt-4 rounded-none bg-primary-orange text-white mono text-[10px] uppercase tracking-widest font-black">
        <Save className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Save connection details"}
      </Button>
    </div>
  );
}
